/**
 * Frames and masks: what the customer does with them.
 *
 * A frame is a picture layer that arrives empty, already seen through a shape.
 * A mask is any shape, text or drawing moved inside a picture layer. In both
 * cases the outline lives on the picture (ImageLayer.mask) in the picture's own
 * frame, so moving, turning and resizing the picture take the outline with it,
 * and releasing puts the outline back on the page exactly where it was seen.
 */

import { templateMode } from '../api';
import type { Asset } from '../assets';
import { toDocumentRef } from '../assets';
import type { Fade, ImageLayer, Layer, Mask, ShapeKind, ShapeLayer } from '../model/types';
import { shapeDef } from '../shapes/library';
import { newId } from './actions';
import { useEditor } from './store';

const BLACK = { space: 'cmyk' as const, value: [ 0, 0, 0, 1 ] as [ number, number, number, number ] };
const FILTERS = { brightness: 0, contrast: 0, saturation: 0, greyscale: false };
const round = ( v: number ) => Math.round( v * 1000 ) / 1000;

/** The shapes offered as frames, in the order the panel shows them. */
export const FRAME_SHAPES: ShapeKind[] = [ 'rect', 'ellipse', 'arch', 'heart', 'star', 'hexagon', 'triangle', 'diamond', 'seal', 'blob', 'shield', 'burst' ];

export type MaskableLayer = NonNullable<Mask[ 'layer' ]>;

export function isMaskable( layer: Layer | undefined | null ): layer is MaskableLayer {
  return !! layer && ( layer.type === 'shape' || layer.type === 'text' || layer.type === 'graphic' );
}

function frameShape( shape: ShapeKind, w: number, h: number ): ShapeLayer {
  return {
    id: newId( 's' ),
    type: 'shape',
    name: shapeDef( shape ).name,
    x: 0,
    y: 0,
    w,
    h,
    rotation: 0,
    opacity: 1,
    locked: false,
    hidden: false,
    shape,
    fill: BLACK,
    stroke: null,
    radius: 0,
    points: shape === 'burst' || shape === 'seal' ? 12 : 5,
  };
}

/** An empty frame in the middle of the page, waiting for a picture. */
export function addFrame( shape: ShapeKind ) {
  const store = useEditor.getState();
  const page = store.page();

  if ( ! page ) return;

  const ratio = shapeDef( shape ).ratio ?? 1;
  const w = round( Math.min( page.size.w * 0.45, page.size.h * 0.7 * ratio, 50 ) );
  const h = round( w / ratio );

  const layer: ImageLayer = {
    id: newId( 'i' ),
    type: 'image',
    name: `${ shapeDef( shape ).name } frame`,
    x: round( ( page.size.w - w ) / 2 ),
    y: round( ( page.size.h - h ) / 2 ),
    w,
    h,
    rotation: 0,
    opacity: 1,
    locked: false,
    hidden: false,
    asset: '',
    fit: 'cover',
    crop: { x: 0, y: 0, w: 1, h: 1 },
    filters: FILTERS,
    mask: { layer: frameShape( shape, w, h ), frame: true, feather: 0, fade: null },

    /*
     * On a template, the frame is part of the design: the customer fills it
     * and moves their picture about inside it, but the frame itself stays
     * where the shop put it. On an ordinary design it is the designer's own
     * and behaves like anything else.
     */
    ...( templateMode() ? { fixed: true } : {} ),
  };

  store.updatePage( 'Add frame', ( p ) => ( { ...p, layers: p.layers.concat( layer ) } ) );
  store.select( [ layer.id ] );
}

/**
 * Puts a picture into the selected frame, when one is selected.
 *
 * @returns Whether it did - when not, the caller places the picture as usual.
 */
/**
 * Picks a frame up and opens Uploads, which is what clicking an empty one does.
 *
 * Selecting it first is what makes the choice land in this frame: the Uploads
 * panel fills whatever frame is selected, so one rule covers the customer who
 * clicks the frame and the one who selects it and goes looking.
 */
export function askForPicture( id: string ) {
  const store = useEditor.getState();

  store.select( [ id ] );
  store.askPanel( 'uploads' );
}

export function fillSelectedFrame( asset: Asset ): boolean {
  const store = useEditor.getState();
  const selected = store.selected();
  const only = selected.length === 1 ? selected[ 0 ] : null;

  if ( ! only || only.type !== 'image' || only.locked || ! only.mask?.frame ) return false;


  store.commit( only.asset ? 'Replace picture' : 'Fill frame', ( doc ) => {
    const pages = doc.pages.map( ( page, index ) =>
      index !== store.pageIndex ? page : { ...page, layers: replace( page.layers, only.id, ( l ) => ( { ...( l as ImageLayer ), asset: asset.id, crop: { x: 0, y: 0, w: 1, h: 1 }, fit: 'cover' } ) ) }
    );
    const known = doc.assets.some( ( ref ) => ref.id === asset.id );

    return { ...doc, pages, assets: known ? doc.assets : doc.assets.concat( toDocumentRef( asset ) ) };
  } );

  return true;
}

export function replace( layers: Layer[], id: string, next: ( layer: Layer ) => Layer ): Layer[] {
  return layers.map( ( layer ) => {
    if ( layer.id === id ) return next( layer );
    if ( layer.type === 'group' ) return { ...layer, children: replace( layer.children, id, next ) };

    return layer;
  } );
}

/** The picture and the outline in a two-object selection, when that is what it is. */
export function maskPair( selected: Layer[] ): { picture: ImageLayer; outline: MaskableLayer } | null {
  if ( selected.length !== 2 ) return null;

  const picture = selected.find( ( l ): l is ImageLayer => l.type === 'image' );
  const outline = selected.find( ( l ) => l !== picture );

  if ( ! picture || ! isMaskable( outline ) || picture.locked || outline.locked ) return null;

  const page = useEditor.getState().page();

  // Both on the page itself: a mask reaching into a group would move two things at once.
  if ( ! page || ! page.layers.includes( picture ) || ! page.layers.includes( outline ) ) return null;

  return { picture, outline };
}

const turn = ( x: number, y: number, degrees: number ) => {
  const a = ( degrees * Math.PI ) / 180;

  return { x: Math.cos( a ) * x - Math.sin( a ) * y, y: Math.sin( a ) * x + Math.cos( a ) * y };
};

const normal = ( degrees: number ) => round( ( ( degrees % 360 ) + 360 ) % 360 );

/** A layer on the page, placed in a picture's own frame instead. */
export function intoFrame( outline: MaskableLayer, picture: ImageLayer ): MaskableLayer {
  const dx = outline.x + outline.w / 2 - ( picture.x + picture.w / 2 );
  const dy = outline.y + outline.h / 2 - ( picture.y + picture.h / 2 );
  const local = turn( dx, dy, -picture.rotation );

  return {
    ...outline,
    x: round( local.x + picture.w / 2 - outline.w / 2 ),
    y: round( local.y + picture.h / 2 - outline.h / 2 ),
    rotation: normal( outline.rotation - picture.rotation ),
  } as MaskableLayer;
}

/** The reverse: a mask, back on the page where it was seen. */
export function outOfFrame( outline: MaskableLayer, picture: ImageLayer ): MaskableLayer {
  const page = turn( outline.x + outline.w / 2 - picture.w / 2, outline.y + outline.h / 2 - picture.h / 2, picture.rotation );

  return {
    ...outline,
    id: newId( outline.type[ 0 ] ?? 'l' ),
    x: round( picture.x + picture.w / 2 + page.x - outline.w / 2 ),
    y: round( picture.y + picture.h / 2 + page.y - outline.h / 2 ),
    rotation: normal( outline.rotation + picture.rotation ),
    hidden: false,
  } as MaskableLayer;
}

/** The picture in the selection, seen through the other object. */
export function maskWithSelection() {
  const store = useEditor.getState();
  const pair = maskPair( store.selected() );

  if ( ! pair ) return;

  const { picture, outline } = pair;

  store.updatePage( 'Mask picture', ( page ) => ( {
    ...page,
    layers: page.layers
      .filter( ( l ) => l.id !== outline.id )
      .map( ( l ) =>
        l.id === picture.id
          ? { ...picture, mask: { layer: intoFrame( outline, picture ), frame: false, feather: picture.mask?.feather ?? 0, fade: picture.mask?.fade ?? null } }
          : l
      ),
  } ) );

  store.select( [ picture.id ] );
}

/**
 * Takes the outline back out: the shape, text or drawing returns to the page
 * above the picture, and the picture is whole again. Soft edges and a fade stay.
 */
export function releaseMask( pictureId: string ) {
  const store = useEditor.getState();
  const page = store.page();
  const picture = page?.layers.find( ( l ): l is ImageLayer => l.id === pictureId && l.type === 'image' );

  if ( ! page || ! picture?.mask?.layer ) return;

  const freed = outOfFrame( picture.mask.layer, picture );
  const soft = picture.mask.feather > 0 || ( picture.mask.fade?.amount ?? 0 ) > 0;
  const rest: Mask | null = soft ? { layer: null, frame: false, feather: picture.mask.feather, fade: picture.mask.fade } : null;

  store.updatePage( picture.mask.frame ? 'Take out of frame' : 'Release mask', ( p ) => ( {
    ...p,
    layers: p.layers.flatMap( ( l ) => ( l.id === picture.id ? [ { ...picture, mask: rest }, freed ] : [ l ] ) ),
  } ) );

  store.select( [ freed.id ] );
}

/** Changes a picture's mask settings: softness, fade. Slider drags are one undo step. */
export function setMask( picture: ImageLayer, patch: Partial<Pick<Mask, 'feather' | 'fade'>>, label = 'Mask' ) {
  useEditor.getState().updateLayer(
    picture.id,
    label,
    ( l ) => {
      const image = l as ImageLayer;
      const mask: Mask = { layer: null, frame: false, feather: 0, fade: null, ...image.mask, ...patch };
      const empty = ! mask.layer && ! ( mask.feather > 0 ) && ! ( ( mask.fade?.amount ?? 0 ) > 0 );

      return { ...image, mask: empty ? null : mask };
    },
    { coalesce: `${ picture.id }:mask:${ Object.keys( patch ).join( ',' ) }` }
  );
}

export function setFade( picture: ImageLayer, fade: Fade | null ) {
  setMask( picture, { fade }, 'Fade' );
}

/** A different shape for a frame, keeping where it sits in the picture. */
export function setFrameShape( picture: ImageLayer, shape: ShapeKind ) {
  const current = picture.mask?.layer;

  if ( ! current || current.type !== 'shape' ) return;

  useEditor.getState().updateLayer( picture.id, 'Frame shape', ( l ) => {
    const image = l as ImageLayer;

    if ( ! image.mask?.layer || image.mask.layer.type !== 'shape' ) return l;

    return {
      ...image,
      mask: { ...image.mask, layer: { ...image.mask.layer, shape, name: shapeDef( shape ).name, nodes: undefined, closed: undefined } },
    };
  } );
}
